import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Link2, ArrowLeftRight, Wallet, Building2,
  ShieldCheck, Key, User, Bell, Settings, LogOut, Menu, ChevronDown,
  Book, Zap, DollarSign, Users, FileText, Activity, Mail, ScrollText,
  UserPlus, Store, IdCard, Package2, ReceiptText, Globe, Clock3,
  Repeat2, QrCode, BriefcaseBusiness, Calculator, MessageSquare,
  Upload, TrendingUp, ArrowUpFromLine
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import useNotificationsWS from '../hooks/useNotificationsWS';
import { getDashboardUrl, getDocsUrl } from '../lib/domains';
import {
  canAccessDashboardRoute, canCreatePaymentLinks,
  canUseApiKeys, canUseWithdrawals, isStaffUser,
} from '../lib/accountPermissions';

// ── Admin item lists ──────────────────────────────────────────────
const adminItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Admin Overview', end: true },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/verifications', icon: ShieldCheck, label: 'Verifications' },
  { to: '/admin/providers', icon: Zap, label: 'Payment Providers' },
  { to: '/admin/settlement-banks', icon: Building2, label: 'Settlement Banks' },
  { to: '/admin/packages', icon: Package2, label: 'Packages' },
  { to: '/admin/subscriptions', icon: ReceiptText, label: 'Subscriptions' },
  { to: '/admin/invoices', icon: FileText, label: 'Invoices' },
  { to: '/admin/receipts', icon: ReceiptText, label: 'Receipts' },
  { to: '/admin/stores', icon: Store, label: 'Store Management' },
  { to: '/admin/products', icon: Package2, label: 'Store Products' },
  { to: '/admin/vcards', icon: IdCard, label: 'vCard Management' },
  { to: '/admin/store-orders', icon: ReceiptText, label: 'Store Orders' },
  { to: '/admin/payment-reminders', icon: Clock3, label: 'Payment Reminders' },
  { to: '/admin/bulk-payment-links', icon: Upload, label: 'Bulk Payment Links' },
  { to: '/admin/custom-domains', icon: Globe, label: 'Custom Domains' },
  { to: '/admin/customers', icon: Users, label: 'Customers' },
  { to: '/admin/commissions', icon: DollarSign, label: 'Commissions' },
  { to: '/admin/email', icon: Mail, label: 'Email Settings' },
  { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs' },
  { to: '/admin/sub-admins', icon: UserPlus, label: 'Sub-Admins' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

const adminBudgetItems = [
  { to: '/admin/budget-users', icon: Users, label: 'Budget Users' },
  { to: '/admin/budget-packages', icon: Package2, label: 'Budget Packages' },
  { to: '/admin/budget-usage', icon: Activity, label: 'Budget Usage' },
  { to: '/admin/budget-reports', icon: FileText, label: 'Budget Reports' },
  { to: '/admin/budget-alerts', icon: Bell, label: 'Budget Alerts' },
];

// ── Budget sub-navigation ────────────────────────────────────────
const budgetNavItems = [
  { to: '/dashboard/budget', icon: LayoutDashboard, label: 'Overview', end: true },
  { to: '/dashboard/budget/transactions', icon: ArrowLeftRight, label: 'Transactions' },
  { to: '/dashboard/budget/expense-requests', icon: FileText, label: 'Expense Requests' },
  { to: '/dashboard/budget/branches', icon: Building2, label: 'Branches' },
  { to: '/dashboard/budget/budgets', icon: Wallet, label: 'Budgets' },
  { to: '/dashboard/budget/allocation', icon: ArrowLeftRight, label: 'Allocation' },
  { to: '/dashboard/budget/loans', icon: Wallet, label: 'Loans' },
  { to: '/dashboard/budget/credit', icon: DollarSign, label: 'Credit' },
  { to: '/dashboard/budget/cash-flow', icon: TrendingUp, label: 'Cash Flow' },
  { to: '/dashboard/budget/reports', icon: FileText, label: 'Reports' },
  { to: '/dashboard/budget/notifications', icon: Bell, label: 'Notifications' },
];

// ── Shared style helpers ──────────────────────────────────────────
function sidebarItemClass(isActive) {
  return `flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg mx-1 transition-colors ${
    isActive
      ? 'bg-emerald-50 text-emerald-700'
      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
  }`;
}

// ── SidebarLink ───────────────────────────────────────────────────
function SidebarLink({ to, icon: Icon, label, end, onItemClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onItemClick}
      data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={({ isActive }) => sidebarItemClass(isActive)}
    >
      <Icon size={16} className="shrink-0" />
      <span className="truncate leading-snug">{label}</span>
    </NavLink>
  );
}

// ── SidebarSection (accordion group) ─────────────────────────────
function SidebarSection({ title, icon: Icon, open, onToggle, active, children }) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-[calc(100%-8px)] items-center justify-between gap-2 px-3 py-2 text-[13px] font-medium rounded-lg mx-1 transition-colors ${
          active ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <Icon size={16} className="shrink-0" />
          <span className="truncate leading-snug">{title}</span>
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="ml-1 pl-1 border-l border-slate-100 space-y-0.5">
          {children}
        </div>
      )}
    </>
  );
}

// ── Determine which group owns the current path ───────────────────
function resolveCurrentGroup(pathname) {
  const p = pathname || '';
  if (p === '/dashboard' || p === '/dashboard/') return 'main';
  if (p.startsWith('/dashboard/transactions') || p.startsWith('/dashboard/analytics')) return 'main';
  if (
    p.startsWith('/dashboard/payment-links') ||
    p.startsWith('/dashboard/invoices') ||
    p.startsWith('/dashboard/receipts') ||
    p.startsWith('/dashboard/subscriptions') ||
    p.startsWith('/dashboard/installments') ||
    p.startsWith('/dashboard/reminders') ||
    p.startsWith('/dashboard/qr-codes') ||
    p.startsWith('/dashboard/bulk-payment-links')
  ) return 'payments';
  if (
    p.startsWith('/dashboard/store') ||
    p.startsWith('/dashboard/pos') ||
    p.startsWith('/dashboard/marketplace') ||
    p.startsWith('/dashboard/delivery-options')
  ) return 'store';
  if (
    p.startsWith('/dashboard/business-profile') ||
    p.startsWith('/dashboard/customers') ||
    p.startsWith('/dashboard/crm') ||
    p.startsWith('/dashboard/quotations') ||
    p.startsWith('/dashboard/staff') ||
    p.startsWith('/dashboard/team') ||
    p.startsWith('/dashboard/budget')
  ) return 'business';
  if (
    p.startsWith('/dashboard/withdrawals') ||
    p.startsWith('/dashboard/bank-details') ||
    p.startsWith('/dashboard/settlement') ||
    p.startsWith('/dashboard/verification')
  ) return 'settlement';
  if (
    p.startsWith('/dashboard/whatsapp-templates') ||
    p.startsWith('/dashboard/vcard') ||
    p.startsWith('/dashboard/custom-domains') ||
    p.startsWith('/dashboard/remove-branding') ||
    p.startsWith('/dashboard/api-keys') ||
    p.startsWith('/dashboard/documentation')
  ) return 'tools';
  if (p.startsWith('/dashboard/package') || p.startsWith('/dashboard/profile')) return 'account';
  return 'main';
}

// ── SidebarNav ────────────────────────────────────────────────────
function SidebarNav({ onItemClick }) {
  const { user } = useAuth();
  const location = useLocation();
  const currentGroup = resolveCurrentGroup(location.pathname);

  // ── Feature flags ──────────────────────────────────────────────
  const premium = user?.premium || user || {};
  // Checks both user.premium.<key> and user.<key> for backwards compat.
  const featureEnabled = (...keys) =>
    keys.some((key) => !!(premium?.[key] ?? user?.[key]));

  const canUseBudget          = featureEnabled('can_use_budget', 'budget');
  const canCreateStore        = featureEnabled('can_create_store', 'store');
  const canCreateVcard        = featureEnabled('can_create_vcard', 'vcard');
  const canCreateInvoices     = featureEnabled('can_create_invoices', 'invoices');
  const canCreateQuotations   = featureEnabled('can_create_quotations', 'quotations');
  const canUseCrm             = featureEnabled('can_use_crm', 'crm');
  const canUseReminders       = featureEnabled('can_use_payment_reminders', 'payment_reminders', 'reminders');
  const canUseSubscriptions   = featureEnabled('can_use_subscriptions', 'subscriptions');
  const canUseBusinessProfile = featureEnabled('can_use_business_profile', 'business_profile');
  const canUseQrCodes         = featureEnabled('can_use_qr_codes', 'qr_codes');
  const canUsePos             = featureEnabled('can_use_pos', 'pos');
  const canUseReceipts        = featureEnabled('can_use_receipts', 'receipts');
  const canUseDeliveryOptions = featureEnabled('can_use_delivery_options', 'delivery_options');
  const canUseAnalytics       = featureEnabled('can_use_analytics', 'analytics');
  const canUseStaffAccounts   = featureEnabled('can_use_staff_accounts', 'staff');
  const canUseWhatsAppTemplates = featureEnabled('can_use_whatsapp_templates', 'whatsapp_templates');
  const canUseBulkPaymentLinks  = featureEnabled('can_use_bulk_payment_links', 'bulk_payment_links', 'bulk_links');
  const canUseMarketplace     = featureEnabled('can_use_marketplace', 'marketplace');
  const canUseCustomDomains   = featureEnabled('custom_domain_allowed', 'can_use_custom_domains', 'custom_domains');
  const canRemoveBranding     = featureEnabled('remove_branding_allowed', 'can_remove_branding', 'remove_branding');
  const apiAccess             = canUseApiKeys(user);
  const canShowApiKeys        = isStaffUser(user) ? canAccessDashboardRoute(user, '/dashboard/api-keys') : apiAccess;
  const canShowDocs           = isStaffUser(user) ? canAccessDashboardRoute(user, '/dashboard/documentation') : apiAccess;
  const isAdmin               = user?.role === 'admin' || !!user?.is_admin;

  // ── Accordion state ────────────────────────────────────────────
  // On first render, open only the group that owns the current URL.
  // (This is 'main' for /dashboard, or another group if deep-linking.)
  // We track firstRender to avoid re-initialising on re-renders.
  const initialised = useRef(false);
  const [openGroup, setOpenGroup] = useState(() => currentGroup);
  const [budgetOpen, setBudgetOpen] = useState(() => location.pathname.startsWith('/dashboard/budget'));
  const [adminOpen, setAdminOpen] = useState(() => location.pathname.startsWith('/admin'));
  const [adminBudgetOpen, setAdminBudgetOpen] = useState(() =>
    location.pathname.startsWith('/admin/budget')
  );

  // Sync open group whenever the URL changes (e.g. back/forward nav or
  // programmatic navigation). Only switches; never collapses the open group.
  useEffect(() => {
    if (!initialised.current) {
      initialised.current = true;
      return; // skip on mount – initial state already set above
    }
    setOpenGroup(currentGroup);
    setBudgetOpen(location.pathname.startsWith('/dashboard/budget'));
    const onAdminPage = location.pathname.startsWith('/admin');
    setAdminOpen(onAdminPage);
    setAdminBudgetOpen(location.pathname.startsWith('/admin/budget'));
  }, [currentGroup, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle: clicking the open group closes it; clicking a different one switches.
  const toggleGroup = (group) =>
    setOpenGroup((prev) => (prev === group ? '' : group));

  const isGroupOpen = (group) => openGroup === group;

  // ── Permission helpers ─────────────────────────────────────────
  const allowed = (to) => {
    if (isStaffUser(user)) return canAccessDashboardRoute(user, to);
    return true;
  };
  const show = (condition, to) =>
    (isStaffUser(user) ? allowed(to) : condition) && allowed(to);

  // ── Item lists ─────────────────────────────────────────────────
  const mainItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Overview', end: true },
    { to: '/dashboard/transactions', icon: ArrowLeftRight, label: 'Transactions' },
    show(canUseAnalytics, '/dashboard/analytics') && {
      to: '/dashboard/analytics', icon: TrendingUp, label: 'Analytics',
    },
  ].filter(Boolean);

  const paymentItems = [
    show(canCreatePaymentLinks(user) || isAdmin, '/dashboard/payment-links') && {
      to: '/dashboard/payment-links', icon: Link2, label: 'Payment Links',
    },
    show(canUseBulkPaymentLinks, '/dashboard/bulk-payment-links') && {
      to: '/dashboard/bulk-payment-links', icon: Upload, label: 'Bulk Payment Links',
    },
    show(canCreateInvoices, '/dashboard/invoices') && {
      to: '/dashboard/invoices', icon: FileText, label: 'Invoices',
    },
    show(canUseReceipts, '/dashboard/receipts') && {
      to: '/dashboard/receipts', icon: ReceiptText, label: 'Receipts',
    },
    show(canUseQrCodes, '/dashboard/qr-codes') && {
      to: '/dashboard/qr-codes', icon: QrCode, label: 'QR Codes',
    },
    show(canUseSubscriptions, '/dashboard/subscriptions') && {
      to: '/dashboard/subscriptions', icon: Repeat2, label: 'Subscriptions',
    },
    show(canUseSubscriptions, '/dashboard/installments') && {
      to: '/dashboard/installments', icon: ReceiptText, label: 'Installment Plans',
    },
    show(canUseReminders, '/dashboard/reminders') && {
      to: '/dashboard/reminders', icon: Clock3, label: 'Payment Reminders',
    },
  ].filter(Boolean);

  const storeItems = [
    show(canCreateStore, '/dashboard/store') && {
      to: '/dashboard/store', icon: Store, label: 'My Store',
    },
    show(canCreateStore, '/dashboard/store/products') && {
      to: '/dashboard/store/products', icon: Package2, label: 'Products',
    },
    show(canCreateStore, '/dashboard/store/orders') && {
      to: '/dashboard/store/orders', icon: ReceiptText, label: 'Store Orders',
    },
    show(canCreateStore, '/dashboard/store/settings') && {
      to: '/dashboard/store/settings', icon: Settings, label: 'Store Settings',
    },
    show(canUseDeliveryOptions && canCreateStore, '/dashboard/delivery-options') && {
      to: '/dashboard/delivery-options', icon: BriefcaseBusiness, label: 'Delivery Options',
    },
    show(canUseCustomDomains && canCreateStore, '/dashboard/store/domains') && {
      to: '/dashboard/store/domains', icon: Globe, label: 'Store Domains',
    },
    show(canUsePos, '/dashboard/pos') && {
      to: '/dashboard/pos', icon: Calculator, label: 'Mini POS',
    },
    show(canUseMarketplace && canCreateStore, '/dashboard/marketplace') && {
      to: '/dashboard/marketplace', icon: Store, label: 'Marketplace',
    },
  ].filter(Boolean);

  // Business items (excluding Budget which is rendered as its own nested accordion)
  const businessItems = [
    show(canUseBusinessProfile, '/dashboard/business-profile') && {
      to: '/dashboard/business-profile', icon: BriefcaseBusiness, label: 'Business Profile',
    },
    show(canUseCrm || canCreateInvoices, '/dashboard/customers') && {
      to: '/dashboard/customers', icon: Users, label: 'Customers',
    },
    show(canUseStaffAccounts, '/dashboard/staff') && {
      to: '/dashboard/staff', icon: Users, label: 'Staff',
    },
    show(canUseCrm, '/dashboard/crm') && {
      to: '/dashboard/crm', icon: Users, label: 'CRM',
    },
    show(canCreateQuotations, '/dashboard/quotations') && {
      to: '/dashboard/quotations', icon: FileText, label: 'Quotations',
    },
  ].filter(Boolean);

  const settlementItems = [
    show(canUseWithdrawals(user) || isAdmin, '/dashboard/withdrawals') && {
      to: '/dashboard/withdrawals', icon: ArrowUpFromLine, label: 'Withdrawals',
    },
    show(canUseWithdrawals(user) || isAdmin, '/dashboard/bank-details') && {
      to: '/dashboard/bank-details', icon: Building2, label: 'Bank Details',
    },
    show(true, '/dashboard/settlement') && {
      to: '/dashboard/settlement', icon: Wallet, label: 'Settlement',
    },
    show(true, '/dashboard/verification') && {
      to: '/dashboard/verification', icon: ShieldCheck, label: 'Verification',
    },
  ].filter(Boolean);

  const toolsItems = [
    show(canUseWhatsAppTemplates, '/dashboard/whatsapp-templates') && {
      to: '/dashboard/whatsapp-templates', icon: MessageSquare, label: 'WhatsApp Templates',
    },
    show(canCreateVcard, '/dashboard/vcard') && {
      to: '/dashboard/vcard', icon: IdCard, label: 'vCard',
    },
    show(canUseCustomDomains, '/dashboard/custom-domains') && {
      to: '/dashboard/custom-domains', icon: Globe, label: 'Custom Domains',
    },
    show(canRemoveBranding, '/dashboard/remove-branding') && {
      to: '/dashboard/remove-branding', icon: ShieldCheck, label: 'Remove Branding',
    },
    show(canShowApiKeys, '/dashboard/api-keys') && {
      to: '/dashboard/api-keys', icon: Key, label: 'API Keys',
    },
  ].filter(Boolean);

  const accountItems = [
    show(true, '/dashboard/package') && {
      to: '/dashboard/package', icon: Package2, label: 'My Package / Subscription',
    },
    show(true, '/dashboard/profile') && {
      to: '/dashboard/profile', icon: User, label: 'Profile',
    },
  ].filter(Boolean);

  const renderItems = (items) =>
    items.map(({ to, icon: Icon, label, end }) => (
      <SidebarLink
        key={to}
        to={to}
        icon={Icon}
        label={label}
        end={end}
        onItemClick={onItemClick}
      />
    ));

  return (
    <nav className="flex h-full min-h-0 flex-col gap-0.5 overflow-y-auto overscroll-contain px-2 py-4 pb-8 [-webkit-overflow-scrolling:touch]">
      {/* Logo */}
      <div className="px-3 mb-4 shrink-0">
        <h1
          className="text-xl font-bold text-emerald-700"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Jjuma Pay
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">Payment Dashboard</p>
      </div>

      {/* ── Main ─────────────────────────────────────────────────── */}
      <SidebarSection
        title="Main"
        icon={LayoutDashboard}
        open={isGroupOpen('main')}
        onToggle={() => toggleGroup('main')}
        active={currentGroup === 'main'}
      >
        {renderItems(mainItems)}
      </SidebarSection>

      {/* ── Payments ─────────────────────────────────────────────── */}
      {paymentItems.length > 0 && (
        <SidebarSection
          title="Payments"
          icon={Link2}
          open={isGroupOpen('payments')}
          onToggle={() => toggleGroup('payments')}
          active={currentGroup === 'payments'}
        >
          {renderItems(paymentItems)}
        </SidebarSection>
      )}

      {/* ── Store ────────────────────────────────────────────────── */}
      {storeItems.length > 0 && (
        <SidebarSection
          title="Store"
          icon={Store}
          open={isGroupOpen('store')}
          onToggle={() => toggleGroup('store')}
          active={currentGroup === 'store'}
        >
          {renderItems(storeItems)}
        </SidebarSection>
      )}

      {/* ── Business ─────────────────────────────────────────────── */}
      {(businessItems.length > 0 || canUseBudget) && (
        <SidebarSection
          title="Business"
          icon={BriefcaseBusiness}
          open={isGroupOpen('business')}
          onToggle={() => toggleGroup('business')}
          active={currentGroup === 'business'}
        >
          {renderItems(businessItems)}

          {/* Budget — nested accordion with all sub-pages */}
          {canUseBudget && (
            <SidebarSection
              title="Budget"
              icon={Wallet}
              open={budgetOpen}
              onToggle={() => setBudgetOpen((prev) => !prev)}
              active={location.pathname.startsWith('/dashboard/budget')}
            >
              {budgetNavItems
                .filter(({ to }) => show(true, to))
                .map(({ to, icon: Icon, label, end }) => (
                  <SidebarLink
                    key={to}
                    to={to}
                    icon={Icon}
                    label={label}
                    end={end}
                    onItemClick={onItemClick}
                  />
                ))}
            </SidebarSection>
          )}
        </SidebarSection>
      )}

      {/* ── Settlement ───────────────────────────────────────────── */}
      {settlementItems.length > 0 && (
        <SidebarSection
          title="Settlement"
          icon={ArrowUpFromLine}
          open={isGroupOpen('settlement')}
          onToggle={() => toggleGroup('settlement')}
          active={currentGroup === 'settlement'}
        >
          {renderItems(settlementItems)}
        </SidebarSection>
      )}

      {/* ── Tools ────────────────────────────────────────────────── */}
      {(toolsItems.length > 0 || canShowDocs) && (
        <SidebarSection
          title="Tools"
          icon={Key}
          open={isGroupOpen('tools')}
          onToggle={() => toggleGroup('tools')}
          active={currentGroup === 'tools'}
        >
          {renderItems(toolsItems)}
          {canShowDocs && (
            <a
              href={getDocsUrl('/')}
              target="_blank"
              rel="noreferrer"
              onClick={onItemClick}
              data-testid="nav-documentation"
              className="flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg mx-1 transition-colors text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              <Book size={16} className="shrink-0" />
              <span className="truncate leading-snug">Documentation</span>
            </a>
          )}
        </SidebarSection>
      )}

      {/* ── Account ──────────────────────────────────────────────── */}
      <SidebarSection
        title="Account"
        icon={User}
        open={isGroupOpen('account')}
        onToggle={() => toggleGroup('account')}
        active={currentGroup === 'account'}
      >
        {renderItems(accountItems)}
      </SidebarSection>

      {/* ── Admin — only shown to admin users ────────────────────── */}
      {isAdmin && (
        <>
          <div className="mx-3 mt-4 mb-1 border-t border-slate-100" />

          <SidebarSection
            title="Admin"
            icon={ShieldCheck}
            open={adminOpen}
            onToggle={() => setAdminOpen((prev) => !prev)}
            active={location.pathname.startsWith('/admin')}
          >
            {/* Budget Management sub-accordion */}
            <SidebarSection
              title="Budget Management"
              icon={Calculator}
              open={adminBudgetOpen}
              onToggle={() => setAdminBudgetOpen((prev) => !prev)}
              active={location.pathname.startsWith('/admin/budget')}
            >
              {adminBudgetItems.map(({ to, icon: Icon, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={onItemClick}
                  data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={({ isActive }) => sidebarItemClass(isActive)}
                >
                  <Icon size={16} className="shrink-0" />
                  <span className="truncate leading-snug">{label}</span>
                </NavLink>
              ))}
            </SidebarSection>

            {/* Regular admin items */}
            {adminItems.map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={onItemClick}
                data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={({ isActive }) => sidebarItemClass(isActive)}
              >
                <Icon size={16} className="shrink-0" />
                <span className="truncate leading-snug">{label}</span>
              </NavLink>
            ))}
          </SidebarSection>
        </>
      )}
    </nav>
  );
}

// ── DashboardLayout ───────────────────────────────────────────────
export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useNotificationsWS(!!user && user !== false);

  const staffRouteAllowed =
    !isStaffUser(user) ||
    (!location.pathname.startsWith('/dashboard/staff') &&
      !location.pathname.startsWith('/dashboard/team') &&
      !location.pathname.startsWith('/dashboard/package') &&
      !location.pathname.startsWith('/kyc') &&
      canAccessDashboardRoute(user, location.pathname));

  useEffect(() => {
    if (document.getElementById('tidio-chat-script')) return undefined;
    const script = document.createElement('script');
    script.id = 'tidio-chat-script';
    script.src = '//code.tidio.co/rxkhi4uzxme2qhgxvxochv5hktjccuyi.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { script.remove(); };
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.assign(getDashboardUrl('/login'));
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* ── Desktop Sidebar ───────────────────────────────────────── */}
      <aside className="w-64 flex-shrink-0 border-r border-slate-200 bg-white hidden md:flex md:flex-col justify-between">
        <SidebarNav />
        <div className="p-4 border-t border-slate-200 shrink-0">
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200 px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[85vw] max-w-sm p-0 h-[100dvh] max-h-[100dvh] overflow-hidden flex flex-col"
              >
                {/* Scrollable nav area */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                  <SidebarNav onItemClick={() => setMobileOpen(false)} />
                </div>
                {/* Sticky logout at bottom of drawer */}
                <div className="p-4 border-t border-slate-200 shrink-0">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors"
                  >
                    <LogOut size={18} />
                    Sign Out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <h1
              className="text-lg font-semibold text-slate-900 md:hidden"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Jjuma Pay
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <NavLink to="/dashboard/notifications" data-testid="header-notifications-btn">
              <Button variant="ghost" size="icon" className="relative">
                <Bell size={18} />
              </Button>
            </NavLink>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden sm:inline">{user?.name || 'User'}</span>
                  <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard/profile')}
                  data-testid="menu-profile"
                >
                  <User size={14} className="mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard/notifications')}
                  data-testid="menu-notifications"
                >
                  <Bell size={14} className="mr-2" /> Notifications
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="menu-logout"
                  className="text-red-600"
                >
                  <LogOut size={14} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          {user?.account_status &&
            user.account_status !== 'approved' &&
            user?.role !== 'admin' &&
            !isStaffUser(user) && (
              <div
                className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2"
                data-testid="pending-verification-banner"
              >
                <ShieldCheck size={16} />
                <span>
                  Your Jjuma Pay account is under review. Complete verification to start
                  receiving payments.
                </span>
              </div>
            )}
          {user?.account_type === 'individual' && !canUseApiKeys(user) && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2">
              <ShieldCheck size={16} />
              <span>API access is available for verified business accounts only.</span>
            </div>
          )}
          <div className="animate-fade-in">
            {staffRouteAllowed ? (
              <Outlet />
            ) : (
              <div className="max-w-2xl">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h2
                    className="text-lg font-semibold text-slate-900"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Access restricted
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Your staff account does not have permission to open this page.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
